"use client";

import { c as _c } from "react/compiler-runtime";
import MemesWaveZapIcon from "@/components/brain/left-sidebar/waves/MemesWaveZapIcon";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import useHasTouchInput from "@/hooks/useHasTouchInput";
import clsx from "clsx";
import { useEffect, useRef } from "react";
import MemesQuickVoteCustomAmountRow from "./MemesQuickVoteCustomAmountRow";
import MemesQuickVoteQuickAmountsRow from "./MemesQuickVoteQuickAmountsRow";
type VoteFeedbackSource = "custom-submit" | "quick-amount";
interface MemesQuickVoteActionBarProps {
  readonly customValue: string;
  readonly feedbackAmount: number | null;
  readonly feedbackSource: VoteFeedbackSource | null;
  readonly isCustomOpen: boolean;
  readonly isSubmitting: boolean;
  readonly isVoteFeedbackActive: boolean;
  readonly latestUsedAmount: number | null;
  readonly quickAmounts: readonly number[];
  readonly uncastPower: number | null;
  readonly votingLabel: string | null;
  readonly onCustomChange: (value: string) => void;
  readonly onCustomSubmit: () => void;
  readonly onOpenCustom: () => void;
  readonly onSkip: () => void;
  readonly onVoteAmount: (amount: number) => void;
}
export default function MemesQuickVoteActionBar(t0) {
  const $ = _c(59);
  const {
    customValue,
    feedbackAmount,
    feedbackSource,
    isCustomOpen,
    isSubmitting,
    isVoteFeedbackActive,
    latestUsedAmount,
    quickAmounts,
    uncastPower,
    votingLabel,
    onCustomChange,
    onCustomSubmit,
    onOpenCustom,
    onSkip,
    onVoteAmount
  } = t0;
  const hasTouchInput = useHasTouchInput();
  const hasQuickAmounts = quickAmounts.length > 0;
  const isCustomRowVisible = !hasQuickAmounts || isCustomOpen;
  const customInputRef = useRef(null);
  const previousCustomRowVisibleRef = useRef(isCustomRowVisible);
  let t1;
  if ($[0] !== customValue) {
    t1 = customValue.trim().length > 0 && Number.parseInt(customValue, 10) > 0 ? formatNumberWithCommas(Number.parseInt(customValue, 10)) : null;
    $[0] = customValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const customAmountLabel = t1;
  let t2;
  let t3;
  if ($[2] !== hasQuickAmounts || $[3] !== hasTouchInput || $[4] !== isCustomRowVisible || $[5] !== isSubmitting) {
    t2 = () => {
      const wasCustomRowVisible = previousCustomRowVisibleRef.current;
      previousCustomRowVisibleRef.current = isCustomRowVisible;
      const justOpenedCustomRow = !wasCustomRowVisible && isCustomRowVisible;
      if (hasTouchInput || !hasQuickAmounts || !justOpenedCustomRow || isSubmitting) {
        return;
      }
      customInputRef.current?.focus();
    };
    t3 = [hasQuickAmounts, hasTouchInput, isCustomRowVisible, isSubmitting];
    $[2] = hasQuickAmounts;
    $[3] = hasTouchInput;
    $[4] = isCustomRowVisible;
    $[5] = isSubmitting;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== isSubmitting || $[9] !== onOpenCustom) {
    t4 = () => {
      if (isSubmitting) {
        return;
      }
      onOpenCustom();
    };
    $[8] = isSubmitting;
    $[9] = onOpenCustom;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleToggleCustom = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="tw-mb-0 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-iron-500">Quick Vote</p>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== uncastPower || $[13] !== votingLabel) {
    t6 = typeof uncastPower === "number" && <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-0.5 tw-py-0.5 tw-text-iron-300 sm:tw-gap-2 sm:tw-py-1"><MemesWaveZapIcon className="tw-size-3.5 tw-flex-shrink-0 tw-fill-primary-400/20 tw-text-primary-400" /><span className="tw-text-xs tw-font-bold tw-tracking-wide"><span className="tw-text-iron-300">{formatNumberWithCommas(uncastPower)}{" "}{votingLabel ?? "votes"} remaining</span></span></div>;
    $[12] = uncastPower;
    $[13] = votingLabel;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-1.5 tw-px-0.5 sm:tw-gap-2 sm:tw-px-1 md:tw-flex-nowrap">{t5}{t6}</div>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== feedbackAmount || $[18] !== feedbackSource || $[19] !== handleToggleCustom || $[20] !== hasQuickAmounts || $[21] !== isCustomOpen || $[22] !== isSubmitting || $[23] !== isVoteFeedbackActive || $[24] !== latestUsedAmount || $[25] !== onVoteAmount || $[26] !== quickAmounts) {
    t8 = hasQuickAmounts && <div aria-hidden={isCustomOpen} className={clsx("tw-absolute tw-inset-0 tw-h-full tw-w-full tw-transform-gpu tw-transition-all tw-duration-300 tw-ease-out motion-reduce:tw-transform-none motion-reduce:tw-transition-none", isCustomOpen ? "tw-pointer-events-none tw-z-0 tw-scale-[0.97] tw-opacity-0" : "tw-z-10 tw-scale-100 tw-opacity-100")}><MemesQuickVoteQuickAmountsRow feedbackAmount={feedbackAmount} feedbackSource={feedbackSource} isSubmitting={isSubmitting} isVoteFeedbackActive={isVoteFeedbackActive} latestUsedAmount={latestUsedAmount} onOpenCustom={handleToggleCustom} onVoteAmount={onVoteAmount} quickAmounts={quickAmounts} /></div>;
    $[17] = feedbackAmount;
    $[18] = feedbackSource;
    $[19] = handleToggleCustom;
    $[20] = hasQuickAmounts;
    $[21] = isCustomOpen;
    $[22] = isSubmitting;
    $[23] = isVoteFeedbackActive;
    $[24] = latestUsedAmount;
    $[25] = onVoteAmount;
    $[26] = quickAmounts;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const t9 = hasQuickAmounts ? !isCustomOpen : undefined;
  const t10 = !hasQuickAmounts || isCustomOpen ? "tw-z-10" : "tw-z-0";
  const t11 = !hasQuickAmounts || isCustomOpen ? "tw-scale-100 tw-opacity-100" : "tw-pointer-events-none tw-scale-[0.97] tw-opacity-0";
  let t12;
  if ($[28] !== t10 || $[29] !== t11) {
    t12 = clsx("tw-absolute tw-inset-0 tw-h-full tw-w-full tw-transform-gpu tw-transition-all tw-duration-300 tw-ease-out motion-reduce:tw-transform-none motion-reduce:tw-transition-none", t10, t11);
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== customAmountLabel || $[32] !== customValue || $[33] !== feedbackSource || $[34] !== handleToggleCustom || $[35] !== hasQuickAmounts || $[36] !== isCustomRowVisible || $[37] !== isSubmitting || $[38] !== isVoteFeedbackActive || $[39] !== onCustomChange || $[40] !== onCustomSubmit || $[41] !== votingLabel) {
    t13 = <MemesQuickVoteCustomAmountRow customAmountLabel={customAmountLabel} customInputRef={customInputRef} customValue={customValue} feedbackSource={feedbackSource} hasQuickAmounts={hasQuickAmounts} isCustomRowVisible={isCustomRowVisible} isSubmitting={isSubmitting} isVoteFeedbackActive={isVoteFeedbackActive} onCustomChange={onCustomChange} onCustomSubmit={onCustomSubmit} onToggleCustom={handleToggleCustom} votingLabel={votingLabel} />;
    $[31] = customAmountLabel;
    $[32] = customValue;
    $[33] = feedbackSource;
    $[34] = handleToggleCustom;
    $[35] = hasQuickAmounts;
    $[36] = isCustomRowVisible;
    $[37] = isSubmitting;
    $[38] = isVoteFeedbackActive;
    $[39] = onCustomChange;
    $[40] = onCustomSubmit;
    $[41] = votingLabel;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== t12 || $[44] !== t13 || $[45] !== t9) {
    t14 = <div aria-hidden={t9} className={t12}>{t13}</div>;
    $[43] = t12;
    $[44] = t13;
    $[45] = t9;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== t14 || $[48] !== t8) {
    t15 = <div className="tw-relative tw-h-11 tw-overflow-hidden sm:tw-h-12 md:tw-h-12">{t8}{t14}</div>;
    $[47] = t14;
    $[48] = t8;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== t15 || $[51] !== t7) {
    t16 = <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-xl tw-border tw-border-solid tw-border-iron-900 tw-bg-iron-950 tw-p-3 tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_12px_28px_rgba(0,0,0,0.22)] sm:tw-gap-3 sm:tw-p-4 md:tw-shadow-[0_20px_40px_rgba(0,0,0,0.18)]">{t7}{t15}</div>;
    $[50] = t15;
    $[51] = t7;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== isSubmitting || $[54] !== onSkip) {
    t17 = <button type="button" onClick={onSkip} disabled={isSubmitting} className="tw-inline-flex tw-h-11 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-900 tw-bg-iron-950 tw-px-5 tw-text-sm tw-font-bold tw-text-iron-200 tw-shadow-[0_10px_24px_rgba(0,0,0,0.18)] tw-transition-all tw-duration-200 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 desktop-hover:hover:tw-border-iron-900 desktop-hover:hover:tw-bg-iron-900 sm:tw-h-12">Skip</button>;
    $[53] = isSubmitting;
    $[54] = onSkip;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== t16 || $[57] !== t17) {
    t18 = <div className="tw-relative tw-bg-[linear-gradient(180deg,rgba(10,10,12,0.68),rgba(10,10,12,0.94))] tw-px-3 tw-pb-[calc(env(safe-area-inset-bottom,0px)+0.375rem)] tw-pt-1.5 tw-backdrop-blur-[28px] sm:tw-pb-[calc(env(safe-area-inset-bottom,0px)+0.5rem)] sm:tw-pt-2 md:tw-relative md:tw-z-20 md:tw-shrink-0 md:tw-p-0 md:tw-px-8 md:tw-pb-6 md:tw-pt-0"><div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-gap-2 sm:tw-gap-3">{t16}{t17}</div></div>;
    $[56] = t16;
    $[57] = t17;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  return t18;
}